<script lang="ts">
    import { onMount } from "svelte";
    import Container from "../../components/Container.svelte";
    import LightTheme from "../../components/LightTheme.svelte";
    import Hero from "../../components/Hero.svelte";

    import type { News } from "../../components/NewsCard.svelte";
    import NewsCard from "../../components/NewsCard.svelte";
    import PageTransition from "../../components/PageTransition.svelte";
    import Headline from "../../components/Headline.svelte";
    import Subline from "../../components/Subline.svelte";
    import { fade } from "svelte/transition";
    import Animate from "../../components/Animate.svelte";
    import { children } from "svelte/internal";
    import Btn from "../../components/Btn.svelte";
    import Divider from "../../components/Divider.svelte";
    import { getLocaleByInt, t } from "../../helpers/translation";

    type News = {
        id: number;
        url: string;
        title: string;
        subline: string;
        image: string;
        date: Date;
        bodyhtml: string;
        shortbodyhtml: string;
        iframehtml: string;
        dark: boolean;
        hidden: boolean;
    };

    // prettier-ignore
    const loadData = async ():Promise<News[]> => {
        const data:News[] = await fetch("/api/NewsSelectGridPublic", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ lang: getLocaleByInt() }),
        })
            .then(data => data.json())
            .then(data => data)

        return data 
        ? Promise.resolve<News[]>(data) 
        : Promise.reject(null);
    };
</script>

<LightTheme />
<Hero headline={$t("page.news.hero.headline")} small />

<Container>
    <Headline>
        {@html $t("page.news.headline")}
    </Headline>
    <Subline>
        {@html $t("page.news.subline")}
    </Subline>

    {#await loadData()}
        {@html $t("fetch.posts.loading")}
    {:then news}
        {#if news.length == 0}
            {@html $t("fetch.posts.empty")}
        {/if}
        <Animate children=".news-card" delay_each={0.2}>
            <div class="news">
                {#each news as data (data.id)}
                    <div class="news-card" class:dark={data.dark}>
                        <div class="inner">
                            <div
                                class="news-image"
                                style="background-image: url('{data.image ||
                                    'https://liveicom.azureedge.net/sinus/img/defaults/sinus_placeholder_dark.svg'}')"
                            >
                                <time class:dark={data.dark}
                                    >{new Date(
                                        data.date,
                                    ).toLocaleDateString()}</time
                                >
                                <!-- <div class:dark={data.dark} class="category">
                                    {data.category_name}
                                </div> -->
                            </div>
                            <div class="content">
                                <h2>{data.title}</h2>
                                <!-- <Divider color={`var(--${data.dark ? "yellow" : "green"})`} /> -->
                                <p>{data.shortbodyhtml}</p>
                            </div>
                            <div class="btn-wrapper">
                                <Btn
                                    href="aktuelles/news/{data.id}/{data.url}"
                                    small
                                    arrow
                                >
                                    {$t("page.news.btn.readmore")}
                                </Btn>
                            </div>
                        </div>
                    </div>
                {:else}
                    {@html $t("page.news.fetch.empty")}
                {/each}
            </div>
        </Animate>
    {:catch}
        <em>{@html $t("fetch.posts.error")}</em>
    {/await}
</Container>

<style lang="scss">
    @import "../../vars";

    .news {
        padding-top: 2em;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 1.5em;
        align-items: space-between;
        @media (max-width: 950px) {
            grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 650px) {
            grid-template-columns: 1fr;
        }
    }

    .news-card {
        --border-radius: 1em;
        width: auto;
        height: auto;
        background-color: $grayLight2;
        align-content: stretch;
        position: relative;
        border-radius: var(--border-radius);
        border-bottom-left-radius: 2rem;
        &.dark {
            background-color: $grayDark;
            h2 {
                color: $yellow !important;
            }
        }
        .news-image {
            border-radius: var(--border-radius) var(--border-radius) 0 0;
            display: block;
            width: 100%;
            padding-top: 55%;
            background-size: cover;
            background-position: center;
            position: relative;
            // &::before {
            //     position: absolute;
            //     width: 100%;
            //     height: 100%;
            //     content: "";
            // }
            time,
            .category {
                position: absolute;
                padding: 0.5em;
                font-size: 0.8em;
                background-color: $grayLight2;
                color: $grayDark;
                &.dark {
                    background-color: $grayDark;
                    color: $grayLight2;
                }
            }
            time {
                bottom: 0;
                left: 0;
                border-top-right-radius: 0.75em;
            }
            .category {
                bottom: 0;
                right: 0;
                border-top-left-radius: 0.75em;
            }
        }
        .inner {
            height: 100%;
            min-height: 300px;
            position: relative;
            display: flex;
            flex-direction: column;
        }
        .btn-wrapper,
        .content {
            padding: 0 1.5em;
            padding-bottom: 1em;
            width: 100%;
        }
        .btn-wrapper {
            // padding-bottom: 2em;
        }
        .content {
            padding-top: 2em;
            height: 100%;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            align-content: flex-start;
            > * {
                display: block;
                width: 100%;
            }
            h2 {
                color: $grayDark;
                margin: 0;
                font-size: 1.5rem;
                font-weight: 600;
                line-height: 1.2;
            }
            p {
                &,
                & * {
                    color: $gray;
                    display: block;
                    // margin: 1em 0;
                    font-size: 1em;
                }
            }
            hr {
                margin-top: 1em;
                margin-bottom: 0.5em;
                border: unset;
                border-bottom: 1px solid $yellow;
            }
            :global(.btn) {
                margin-top: auto;
                max-width: max-content;
                align-self: flex-end;
            }
        }
    }
</style>
